import { Loader, Tooltip, VisuallyHidden } from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import * as React from "react";
import type { SubmitStatus } from "../types";
import { downloadSerialTemplate } from "./SerialExcel";

interface ReturnInwardToolbarProps {
  isSmallScreen: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  editDisabled: boolean;
  hasRows: boolean;
  selectedCount: number;
  onDeleteSelected: () => void;
  submitting: SubmitStatus | null;
  onSubmit: (status: SubmitStatus) => void;
  /** Số vướng mắc còn chặn hoàn thành, hiện trên nút Hoàn thành khi > 0 */
  issueCount: number;
  /** Có nút in phiếu (phiếu đã khoá và loại phiếu có mẫu in) */
  canPrint: boolean;
  printing: boolean;
  onPrint: () => void;
}

type ButtonTone = "plain" | "blue" | "danger" | "success";

const TONE_STYLE: Record<ButtonTone, React.CSSProperties> = {
  plain: { padding: "0 12px", border: "1px solid #D5D9D2", background: "#FFFFFF", color: "#1B1F24", fontWeight: 500 },
  blue: { padding: "0 14px", border: "1px solid #C9D6E8", background: "#FFFFFF", color: "#1F5FAE", fontWeight: 600 },
  danger: { padding: "0 12px", border: "1px solid #F2D4D1", background: "#FFFFFF", color: "#B42318", fontWeight: 500 },
  success: { padding: "0 14px", border: 0, background: "#1E6B46", color: "#FFFFFF", fontWeight: 600 },
};

/** Nút xoá bị khoá giữ màu nhạt của thiết kế; các nút khác mờ đi */
const DISABLED_STYLE: Partial<Record<ButtonTone, React.CSSProperties>> = {
  danger: { border: "1px solid #ECD2CF", color: "#B7A5A2" },
};

interface ToolbarButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  tone: ButtonTone;
  icon?: React.ReactNode;
  loading?: boolean;
}

/** Nút cao 36, bo 8 theo thiết kế */
const ToolbarButton = ({ tone, icon, loading = false, disabled, children, ...rest }: ToolbarButtonProps) => {
  const blocked = !!disabled || loading;
  const disabledStyle = disabled && !loading ? DISABLED_STYLE[tone] ?? { opacity: 0.5 } : null;
  return (
    <button
      type="button"
      disabled={blocked}
      aria-busy={loading || undefined}
      {...rest}
      style={{
        height: 36,
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 8,
        font: "inherit",
        fontSize: 13,
        whiteSpace: "nowrap",
        flexShrink: 0,
        cursor: blocked ? "not-allowed" : "pointer",
        ...TONE_STYLE[tone],
        ...disabledStyle,
        ...(loading ? { cursor: "progress" } : null),
      }}
    >
      {loading ? <Loader size={15} color="currentColor" /> : icon}
      {children}
    </button>
  );
};

const SaveIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 3h11l3 3v15H5z" />
    <path d="M8 3v6h8V3" />
    <path d="M8 21v-7h8v7" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5L20 7" />
  </svg>
);

/** Thanh nút: Thông tin chung, Tải file mẫu, Xóa đã chọn, In phiếu nhập kho, Lưu, Hoàn thành */
const ReturnInwardToolbar = ({
  isSmallScreen,
  collapsed,
  onToggleCollapse,
  editDisabled,
  hasRows,
  selectedCount,
  onDeleteSelected,
  submitting,
  onSubmit,
  issueCount,
  canPrint,
  printing,
  onPrint,
}: ReturnInwardToolbarProps) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 12 }}>
    {!isSmallScreen && (
      <Tooltip label="Ctrl + Shift + Z" position="bottom">
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#4A525C",
            fontSize: 12,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <input
            type="checkbox"
            checked={!collapsed}
            onChange={onToggleCollapse}
            style={{ width: 16, height: 16, margin: 0, accentColor: "#1E6B46", cursor: "pointer" }}
          />
          Thông tin chung
        </label>
      </Tooltip>
    )}

    <ToolbarButton tone="plain" onClick={downloadSerialTemplate}>
      Tải file mẫu
    </ToolbarButton>

    <ToolbarButton tone="danger" onClick={onDeleteSelected} disabled={editDisabled || selectedCount === 0}>
      Xóa đã chọn ({selectedCount})
    </ToolbarButton>

    {canPrint && (
      <ToolbarButton tone="blue" icon={<IconPrinter size={15} />} loading={printing} onClick={onPrint}>
        In phiếu nhập kho
      </ToolbarButton>
    )}

    <ToolbarButton
      tone="blue"
      icon={<SaveIcon />}
      loading={submitting === "OPEN"}
      disabled={editDisabled || !hasRows || (submitting !== null && submitting !== "OPEN")}
      onClick={() => onSubmit("OPEN")}
    >
      Lưu (Ctrl+S)
    </ToolbarButton>
    <ToolbarButton
      tone="success"
      icon={<CheckIcon />}
      loading={submitting === "LOCK"}
      disabled={editDisabled || !hasRows || (submitting !== null && submitting !== "LOCK")}
      onClick={() => onSubmit("LOCK")}
    >
      Hoàn thành (Ctrl+Q)
      {issueCount > 0 && (
        <span
          style={{
            padding: "1px 7px",
            borderRadius: 999,
            background: "#FFFFFF",
            color: "#8A4B05",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "16px",
          }}
        >
          {issueCount}
          <VisuallyHidden> vướng mắc</VisuallyHidden>
        </span>
      )}
    </ToolbarButton>
  </div>
);

export default ReturnInwardToolbar;
